// Database health for the active env — connections, size, top tables.
// Renders a "not configured" hint when SUPABASE_MGMT_TOKEN isn't set
// so we don't show empty data as if it's accurate.

interface TableSize {
  schema: string;
  table: string;
  rowEstimate: number;
  totalBytes: number;
}

interface DbHealth {
  env: 'dev' | 'staging' | 'prod';
  configured: boolean;
  reason?: string;
  connectionsActive: number;
  connectionsIdle: number;
  connectionsTotal: number;
  maxConnections: number | null;
  dbSizeBytes: number;
  topTables: TableSize[];
  error?: string;
}

const ENV_LABEL: Record<DbHealth['env'], string> = {
  dev: 'Dev',
  staging: 'Staging',
  prod: 'Prod',
};

export function DbHealthBlock({ data }: { data: DbHealth }) {
  if (!data.configured) {
    return (
      <section className="border border-slate-800 rounded-lg p-4">
        <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-300 mb-2">
          Database health ({ENV_LABEL[data.env]})
        </h3>
        <p className="text-xs text-slate-500 italic">
          {data.reason || 'Supabase Management API not configured on this hub deployment.'}
        </p>
      </section>
    );
  }

  const connPct = data.maxConnections
    ? data.connectionsTotal / data.maxConnections
    : 0;
  const connTone =
    connPct >= 0.8 ? 'bg-rose-500' : connPct >= 0.5 ? 'bg-amber-400' : 'bg-emerald-400';

  return (
    <section className="border border-slate-800 rounded-lg overflow-hidden">
      <div className="bg-slate-900/40 px-4 py-2.5 border-b border-slate-800">
        <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-300">
          Database health ({ENV_LABEL[data.env]})
        </h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-800">
        <div className="bg-slate-950/40 p-3">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400">
            Connections
          </div>
          <div className="text-2xl font-black text-slate-100 mt-1">
            {data.connectionsTotal}
            {data.maxConnections && (
              <span className="text-sm font-normal text-slate-500 ml-1">
                / {data.maxConnections}
              </span>
            )}
          </div>
          {data.maxConnections && (
            <div className="w-full h-1.5 bg-slate-800 rounded overflow-hidden mt-1.5">
              <div
                className={`h-full ${connTone}`}
                style={{ width: `${Math.min(100, Math.round(connPct * 100))}%` }}
              />
            </div>
          )}
          <div className="text-[10px] text-slate-500 mt-1 font-mono">
            <span className="text-emerald-400">{data.connectionsActive}</span> active ·{' '}
            <span className="text-slate-400">{data.connectionsIdle}</span> idle
          </div>
        </div>

        <div className="bg-slate-950/40 p-3">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400">
            Database size
          </div>
          <div className="text-2xl font-black text-slate-100 mt-1">
            {formatBytes(data.dbSizeBytes)}
          </div>
          <div className="text-[10px] text-slate-500 mt-1">total on disk</div>
        </div>

        <div className="bg-slate-950/40 p-3">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-1">
            Top tables
          </div>
          <ul className="space-y-0.5 text-[11px]">
            {data.topTables.slice(0, 5).map((t) => (
              <li
                key={`${t.schema}.${t.table}`}
                className="flex items-center justify-between gap-2"
              >
                <span className="truncate text-slate-300 font-mono">
                  {t.schema === 'public' ? '' : `${t.schema}.`}
                  {t.table}
                </span>
                <span className="text-[10px] text-slate-500 font-mono whitespace-nowrap">
                  {formatBytes(t.totalBytes)} ·{' '}
                  {t.rowEstimate >= 0
                    ? t.rowEstimate.toLocaleString()
                    : '?'}{' '}
                  rows
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function formatBytes(bytes: number): string {
  if (!bytes || bytes < 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}
